import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Field, Form } from 'react-final-form';

import { Button, Divider, TextField, Typography } from '@mui/material';
import { Dialog, DialogActions, DialogContent } from 'material/components/Dialog';
import Grid from 'material/components/Grid';

import { fetchApiKeys, postApiKey } from 'store/actions/organizations';
import { organizationSelectors } from 'store/selectors/organizations';

import { useQuery } from 'utils/helpers';
import { useCopyToClipboard } from 'utils/hooks';
import { TabPanel, ZapierComponent } from 'common/components';
import { SettingsWrapper } from 'globalStyles';
import mediaQueries from 'utils/mediaQueries';
import ApiKeysTable from '../ApiKeysTable';
import VirtuousIntegration from '../VirtuousIntegration';

const StyledButton = styled(Button)`
  margin-bottom: 40px;
`;

const StyledGrid = styled(Grid)`
  margin-top: 20px;
`;

const StyledDivider = styled(Divider)`
  display: block;
  margin-bottom: 20px;
`;

const StyledField = styled(Field)`
  width: 50%;
  padding-top: 20px;
`;

const KeyDialogWrapper = styled.div`
  @media ${mediaQueries.xsmall} {
    flex-direction: column;
  }
  overflow: hidden;
  display: flex;
  gap: 32px;
  word-wrap: break-word;
  word-break: break-word;
`;

const ZapierComponentWrapper = styled.div`
  margin-top: 20px;
`;

const KeyValue = styled(Typography)`
  font-weight: 500;
`;

const TabsWrapper = styled.div`
  display: inline-flex;
  gap: 14px;
  margin-bottom: 64px;
  border-radius: 8px;
  padding: 8px 12px;
  background-color: ${({ theme }) => theme.colors.lightGreenBackground};
`;

const TabButton = styled(Button)`
  padding: 8px 30px;
  background-color: ${({ $isPicked, theme }) =>
    $isPicked ? theme.colors.secondary : theme.colors.lightGreenBackground};
  color: ${({ $isPicked, theme }) => ($isPicked ? theme.colors.white : theme.colors.secondary)};
`;

const IntegrationsTab = ({ flags }) => {
  const { organizationUuid } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const query = useQuery();
  const tabIndex = query.get('integration');
  const [copy] = useCopyToClipboard();
  const [oneTimeVisibleKey, setOneTimeVisibleKey] = useState('');

  const { apiKeysData, organization } = useSelector(state => ({
    apiKeysData: organizationSelectors.getApiKeysDetails(state),
    organization: organizationSelectors.getOrganization(state),
  }));

  useEffect(() => {
    if (tabIndex !== 'virtuous') {
      dispatch(fetchApiKeys(organizationUuid, `scope=${tabIndex}`));
    }
  }, [tabIndex]);

  const handleCreateNewApiKey = async values => {
    try {
      const apiKeyData = await dispatch(postApiKey(organizationUuid, values));

      await dispatch(fetchApiKeys(organizationUuid, `scope=${tabIndex}`)).finally(() =>
        setOneTimeVisibleKey(apiKeyData.key)
      );

      return 'success';
    } catch (error) {
      return error;
    }
  };

  const handleCopyToClipboard = () => {
    copy(oneTimeVisibleKey, 'input', 'API key');
  };

  const handleSetTab = newValue => {
    history.push({
      search: `?tab=integrations&integration=${newValue}`,
    });
  };

  const integrationsTabs = [
    {
      disabled: false,
      label: 'Zapier',
      value: 'zapier',
    },
    {
      disabled: flags ? !flags.usesOpenApi : true,
      label: 'API',
      value: 'api',
    },
    {
      disabled: flags ? !flags.showSalesforce : true,
      label: 'Salesforce',
      value: 'salesforce',
    },
    {
      disabled: flags ? !flags.showVirtuous : true,
      label: 'Virtuous',
      value: 'virtuous',
    },
  ];

  const salesforceKeyNotice = organization?.isDemo ? (
    <>
      This is a demo account; this API key should <strong>not</strong> be used in production
      instances.{' '}
      {flags?.showLists
        ? 'Please restrict to sandboxes with fewer than 5,000 contacts.'
        : 'Please restrict to sandbox installation.'}
    </>
  ) : (
    'This is a production account; this API key should only be used in production instances.'
  );

  const oneTimeKeyDialog = () => (
    <Dialog
      aria-labelledby="one-time-key-title"
      fullWidth
      maxWidth="sm"
      open={Boolean(oneTimeVisibleKey)}
    >
      <DialogContent>
        <KeyDialogWrapper>
          <div>
            <img
              loading="eager"
              alt="secure_icon"
              src="/secure_icon.svg"
              width="90"
              height="90"
              style={{ display: 'block' }}
            />
          </div>
          <div>
            <Typography id="one-time-key-title" variant="h5">
              Important!
            </Typography>
            <Typography variant="body1" color="grey">
              This key is displayed only once. Please download or copy it now, as you won&apos;t be
              able to access it after closing this window. Your new key:
            </Typography>
            <KeyValue>{oneTimeVisibleKey}</KeyValue>
            {tabIndex === 'salesforce' && (
              <Typography
                variant="body1"
                color="grey"
                sx={{
                  marginTop: '16px',
                }}
              >
                {salesforceKeyNotice}
              </Typography>
            )}
          </div>
        </KeyDialogWrapper>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={() => setOneTimeVisibleKey('')}>
          Close
        </Button>
        <Button
          color="secondary"
          disableElevation
          onClick={handleCopyToClipboard}
          variant="contained"
        >
          Copy API key
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <SettingsWrapper>
      <StyledGrid>
        <TabsWrapper>
          <TabButton
            $isPicked={tabIndex === integrationsTabs[0].value}
            disableElevation
            onClick={() => handleSetTab(integrationsTabs[0].value)}
            color="secondary"
            variant="contained"
          >
            Zapier
          </TabButton>
          {flags?.usesOpenApi && (
            <TabButton
              $isPicked={tabIndex === integrationsTabs[1].value}
              disableElevation
              onClick={() => handleSetTab(integrationsTabs[1].value)}
              color="secondary"
              variant="contained"
            >
              Open API
            </TabButton>
          )}
          {flags?.showSalesforce && (
            <TabButton
              $isPicked={tabIndex === integrationsTabs[2].value}
              disableElevation
              onClick={() => handleSetTab(integrationsTabs[2].value)}
              color="secondary"
              variant="contained"
            >
              Salesforce
            </TabButton>
          )}
          {flags?.showVirtuous && (
            <TabButton
              $isPicked={tabIndex === integrationsTabs[3].value}
              disableElevation
              onClick={() => handleSetTab(integrationsTabs[3].value)}
              color="secondary"
              variant="contained"
            >
              Virtuous
            </TabButton>
          )}
        </TabsWrapper>
        <TabPanel index={integrationsTabs[2].value} value={tabIndex || false}>
          <Typography variant="h6" gutterBottom>
            Create a new key
          </Typography>
          <Form
            onSubmit={handleCreateNewApiKey}
            initialValues={{ scope: 'salesforce' }}
            render={({ handleSubmit, pristine, submitting }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid size={6}>
                    <StyledField name="name">
                      {({ input, meta }) => (
                        <TextField
                          {...input}
                          error={Boolean(meta.touched && (meta.error || meta.submitError))}
                          fullWidth
                          helperText={meta.touched && (meta.error || meta.submitError)}
                          label="Key name"
                          type="text"
                        />
                      )}
                    </StyledField>
                  </Grid>
                  <Grid size={3}>
                    <StyledButton
                      color="primary"
                      disabled={submitting || pristine}
                      disableElevation
                      type="submit"
                      variant="contained"
                    >
                      Create
                    </StyledButton>
                  </Grid>
                </Grid>
              </form>
            )}
          />
          <StyledDivider />
          <Typography variant="h6" gutterBottom>
            Salesforce keys
          </Typography>
          <ApiKeysTable
            apiKeysData={apiKeysData}
            organizationUuid={organizationUuid}
            scope="salesforce"
          />
        </TabPanel>
        <TabPanel index={integrationsTabs[0].value} value={tabIndex || false}>
          <Typography variant="h6" gutterBottom>
            Create a new key
          </Typography>
          <Form
            onSubmit={handleCreateNewApiKey}
            initialValues={{ scope: 'zapier' }}
            render={({ handleSubmit, pristine, submitting }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid size={6}>
                    <StyledField name="name">
                      {({ input, meta }) => (
                        <TextField
                          {...input}
                          error={Boolean(meta.touched && (meta.error || meta.submitError))}
                          fullWidth
                          helperText={meta.touched && (meta.error || meta.submitError)}
                          label="Key name"
                          type="text"
                        />
                      )}
                    </StyledField>
                  </Grid>
                  <Grid size={3}>
                    <StyledButton
                      color="secondary"
                      disabled={submitting || pristine}
                      disableElevation
                      type="submit"
                      variant="contained"
                    >
                      Create
                    </StyledButton>
                  </Grid>
                </Grid>
              </form>
            )}
          />
          <StyledDivider />
          <Typography variant="h6" gutterBottom>
            Zapier Keys
          </Typography>
          <ApiKeysTable
            apiKeysData={apiKeysData}
            organizationUuid={organizationUuid}
            scope="zapier"
          />
          <ZapierComponentWrapper>
            <Typography variant="h6" gutterBottom>
              Edit and Add Zaps
            </Typography>
            <StyledDivider />
            <ZapierComponent />
          </ZapierComponentWrapper>
        </TabPanel>
        <TabPanel index={integrationsTabs[1].value} value={tabIndex || false}>
          <Typography variant="h6" gutterBottom>
            Create a new key
          </Typography>
          <Form
            onSubmit={handleCreateNewApiKey}
            initialValues={{ scope: 'api' }}
            render={({ handleSubmit, pristine, submitting }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid size={6}>
                    <StyledField name="name">
                      {({ input, meta }) => (
                        <TextField
                          {...input}
                          error={Boolean(meta.touched && (meta.error || meta.submitError))}
                          fullWidth
                          helperText={meta.touched && (meta.error || meta.submitError)}
                          label="Key name"
                          type="text"
                        />
                      )}
                    </StyledField>
                  </Grid>
                  <Grid size={3}>
                    <StyledButton
                      color="primary"
                      disabled={submitting || pristine}
                      disableElevation
                      type="submit"
                      variant="contained"
                    >
                      Create
                    </StyledButton>
                  </Grid>
                </Grid>
              </form>
            )}
          />
          <StyledDivider />
          <Typography variant="h6" gutterBottom>
            API Keys
          </Typography>
          <ApiKeysTable apiKeysData={apiKeysData} organizationUuid={organizationUuid} scope="api" />
        </TabPanel>
        <TabPanel index={integrationsTabs[3].value} value={tabIndex || false}>
          <VirtuousIntegration organizationUuid={organizationUuid} />
        </TabPanel>
        {oneTimeKeyDialog()}
      </StyledGrid>
    </SettingsWrapper>
  );
};

IntegrationsTab.defaultProps = {
  flags: null,
};

IntegrationsTab.propTypes = {
  flags: PropTypes.shape({
    showSalesforce: PropTypes.bool,
    showVirtuous: PropTypes.bool,
    usesOpenApi: PropTypes.bool,
  }),
};

export default IntegrationsTab;
